import { Check, Trash2, X } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useLang } from "@/context/LangContext";
import { StatusBadge } from "@/components/StatusBadge";
import { AuthImage } from "@/components/AuthImage";
import { ReceiptPreview } from "@/components/ReceiptPreview";

const BTN = "flex h-8 w-8 items-center justify-center rounded-full border transition-colors";

const IconBtn = ({ onClick, testId, title, cls, children }) => (
  <button type="button" onClick={onClick} data-testid={testId} title={title} className={`${BTN} ${cls}`}>{children}</button>
);

const DeleteButton = ({ user, onConfirm }) => {
  const { t } = useLang();
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid={`admin-delete-button-${user.id}`} title={t("delete")} className={`${BTN} border-rose-300 text-rose-700 hover:bg-rose-100 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-950`}><Trash2 className="h-4 w-4" /></button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
          <AlertDialogDescription>{user.full_name} — {t("confirm_delete_desc")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="admin-delete-cancel">{t("cancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid="admin-delete-confirm" className="bg-rose-600 text-white hover:bg-rose-700">{t("delete")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const ReceiptCell = ({ u, onReceipt }) => {
  const { t } = useLang();
  if (!u.payment_receipt_path) return <span className="text-xs text-muted-foreground" data-testid={`admin-no-receipt-${u.id}`}>{t("no_receipt")}</span>;
  if (u.payment_receipt_type === "application/pdf") return <ReceiptPreview path={u.payment_receipt_path} type={u.payment_receipt_type} testId={`admin-view-receipt-${u.id}`} />;
  return (
    <button onClick={() => onReceipt(u)} data-testid={`admin-view-receipt-${u.id}`}>
      <AuthImage path={u.payment_receipt_path} alt="receipt" className="h-11 w-14 rounded-lg border border-border object-cover transition-transform hover:scale-110" />
    </button>
  );
};

export const UserRow = ({ u, onView, onReceipt, onStatus, onDelete }) => {
  const { t } = useLang();
  return (
    <tr className="border-b border-border transition-colors last:border-0 hover:bg-muted/40" data-testid={`admin-user-row-${u.id}`}>
      <td className="px-4 py-3">
        <button onClick={() => onView(u)} data-testid={`admin-view-photo-${u.id}`}>
          <AuthImage path={u.id_photo_path} alt="ID" className="h-11 w-14 rounded-lg border border-border object-cover transition-transform hover:scale-110" />
        </button>
      </td>
      <td className="px-4 py-3"><ReceiptCell u={u} onReceipt={onReceipt} /></td>
      <td className="px-4 py-3 font-semibold">{u.full_name}</td>
      <td className="px-4 py-3 font-mono text-xs">{u.phone}</td>
      <td className="px-4 py-3">{u.country}</td>
      <td className="px-4 py-3">{u.package_name}</td>
      <td className="px-4 py-3"><StatusBadge status={u.status} testId={`admin-status-${u.id}`} /></td>
      <td className="px-4 py-3 text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</td>
      <td className="px-4 py-3">
        <div className="flex justify-end gap-2">
          <IconBtn onClick={() => onStatus(u.id, "approved")} testId={`admin-approve-button-${u.id}`} title={t("approve")} cls="border-emerald-300 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-950"><Check className="h-4 w-4" /></IconBtn>
          <IconBtn onClick={() => onStatus(u.id, "rejected")} testId={`admin-reject-button-${u.id}`} title={t("reject")} cls="border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-400 dark:hover:bg-amber-950"><X className="h-4 w-4" /></IconBtn>
          <DeleteButton user={u} onConfirm={() => onDelete(u.id)} />
        </div>
      </td>
    </tr>
  );
};
